/**
 * PACIENTES PAGE - Patient Management with useServerDataTable hook
 * Gestión de pacientes del laboratorio
 *
 * MIGRADO A useServerDataTable para:
 * - Columnas ordenables (sorting)
 * - Selector de filas por página
 * - Toggle visibilidad de columnas
 * - Persistencia en localStorage
 * - Sincronización con URL
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Users,
  Plus,
  MoreHorizontal,
  Pencil,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Search,
  X,
  FileSignature,
  Activity,
  Paperclip,
  ClipboardList,
  Phone,
  History,
} from "lucide-react";
import { PatientAvatar } from "@/components/ui/avatar";
import { ResponsiveTable, useIsMobile, useIsTablet } from "@/components/ui/responsive-table";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import {
  usePaciente,
  useActivatePaciente,
  useDeactivatePaciente,
  useDeletePaciente,
  type PacienteListItem,
} from "@/hooks/use-pacientes";
import { getPacientes } from "@/hooks/use-pacientes";

import { PacienteFormDialog } from "./components/paciente-form-dialog";
import { ConsentimientosList } from "./components/consentimientos-list";
import { PatologiasList } from "./components/patologias-list";
import { ArchivosAdjuntosList } from "./components/archivos-adjuntos-list";
import { BusquedaAvanzadaSheet } from "@/pages/busqueda-avanzada/components/busqueda-avanzada-sheet";

export default function PacientesPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const navigate = useNavigate();
  const location = useLocation();

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConsentimientosOpen, setIsConsentimientosOpen] = useState(false);
  const [isPatologiasOpen, setIsPatologiasOpen] = useState(false);
  const [isArchivosOpen, setIsArchivosOpen] = useState(false);
  const [selectedPacienteId, setSelectedPacienteId] = useState<number | null>(null);
  const [selectedPacienteForAction, setSelectedPacienteForAction] =
    useState<PacienteListItem | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );
  const [isCreateOrdenPromptOpen, setIsCreateOrdenPromptOpen] = useState(false);
  const [newlyCreatedPaciente, setNewlyCreatedPaciente] = useState<{
    id: number;
    nombre_completo: string;
    identificacion?: string;
    edad_texto?: string;
    edad_years?: number;
    celular?: string;
  } | null>(null);

  // Historial de búsqueda avanzada
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialPaciente, setHistorialPaciente] = useState<PacienteListItem | null>(null);

  // Abrir edición de paciente si viene de otra página con editPacienteId
  useEffect(() => {
    const state = location.state as { editPacienteId?: number } | null;
    if (state?.editPacienteId) {
      setSelectedPacienteId(state.editPacienteId);
      setIsFormOpen(true);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  // Fetch full paciente data for editing
  const { data: selectedPaciente } = usePaciente(selectedPacienteId || 0, {
    enabled: !!selectedPacienteId,
  });

  // Only show form dialog when data is ready (or when creating new)
  const isFormReady = selectedPacienteId === null || !!selectedPaciente;

  // Mutations
  const activateMutation = useActivatePaciente({
    onSuccess: () => {
      toast.success(
        t("pacientes.toast.activated", { name: selectedPacienteForAction?.nombre_completo })
      );
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deactivateMutation = useDeactivatePaciente({
    onSuccess: () => {
      toast.warning(
        t("pacientes.toast.deactivated", { name: selectedPacienteForAction?.nombre_completo })
      );
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeletePaciente({
    onSuccess: () => {
      toast.success(
        t("pacientes.toast.deleted", { name: selectedPacienteForAction?.nombre_completo })
      );
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Handlers
  const handleEdit = useCallback((paciente: PacienteListItem) => {
    setSelectedPacienteId(paciente.id);
    setIsFormOpen(true);
  }, []);

  const handleViewConsentimientos = useCallback((paciente: PacienteListItem) => {
    setSelectedPacienteForAction(paciente);
    setIsConsentimientosOpen(true);
  }, []);

  const handleViewPatologias = useCallback((paciente: PacienteListItem) => {
    setSelectedPacienteForAction(paciente);
    setIsPatologiasOpen(true);
  }, []);

  const handleViewArchivos = useCallback((paciente: PacienteListItem) => {
    setSelectedPacienteForAction(paciente);
    setIsArchivosOpen(true);
  }, []);

  const handleToggleStatus = useCallback((paciente: PacienteListItem) => {
    setSelectedPacienteForAction(paciente);
    setConfirmAction(paciente.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((paciente: PacienteListItem) => {
    setSelectedPacienteForAction(paciente);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleCreate = () => {
    setSelectedPacienteId(null);
    setIsFormOpen(true);
  };

  const handleNuevaOrden = useCallback(
    (paciente: PacienteListItem) => {
      navigate("/ordenes", {
        state: {
          pacienteId: paciente.id,
          pacienteNombre: paciente.nombre_completo,
          pacienteDesc: [
            paciente.identificacion,
            paciente.edad_texto,
            paciente.celular ? `Tel: ${paciente.celular}` : null,
          ]
            .filter(Boolean)
            .join(" · "),
          pacienteEdadYears: paciente.edad_years,
        },
      });
    },
    [navigate]
  );

  const handleVerHistorial = useCallback((paciente: PacienteListItem) => {
    setHistorialPaciente(paciente);
    setHistorialOpen(true);
  }, []);

  const handleConfirmAction = () => {
    if (!selectedPacienteForAction || !confirmAction) return;

    if (confirmAction === "activate") {
      activateMutation.mutate(selectedPacienteForAction.id);
    } else if (confirmAction === "deactivate") {
      deactivateMutation.mutate(selectedPacienteForAction.id);
    } else if (confirmAction === "delete") {
      deleteMutation.mutate(selectedPacienteForAction.id);
    }

    setIsConfirmOpen(false);
    setSelectedPacienteForAction(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (
    isNew: boolean,
    createdPaciente?: {
      id: number;
      nombre_completo: string;
      identificacion?: string;
      edad_texto?: string;
      edad_years?: number;
      celular?: string;
    }
  ) => {
    setIsFormOpen(false);
    toast.success(isNew ? t("pacientes.toast.created") : t("pacientes.toast.updated"));
    setSelectedPacienteId(null);
    refetch();

    if (isNew && createdPaciente) {
      setNewlyCreatedPaciente(createdPaciente);
      setIsCreateOrdenPromptOpen(true);
    }
  };

  // Helper para abreviar tipo de documento en tablet
  const abreviarIdentificacion = (identificacion: string): string => {
    return identificacion
      .replace("Documento Nacional de Identidad", "DNI")
      .replace("Partida de Nacimiento", "P/N")
      .replace("Sin Identificación", "S/I")
      .replace("Pasaporte", "PAS");
  };

  // Column definitions with sortable headers
  const columns: ColumnDef<PacienteListItem>[] = useMemo(
    () => [
      {
        id: "foto",
        header: "",
        cell: ({ row }) => (
          <PatientAvatar
            src={row.original.foto_documento_url}
            name={row.original.nombre_completo}
            sexo={row.original.sexo}
            size="sm"
          />
        ),
        size: 50,
        enableSorting: false,
      },
      {
        accessorKey: "identificacion",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("pacientes.columns.identification")} />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm" title={row.original.identificacion}>
            {/* Versión abreviada solo en tablet para ahorrar espacio */}
            {isTablet
              ? abreviarIdentificacion(row.original.identificacion)
              : row.original.identificacion}
          </span>
        ),
        enableSorting: false, // Campo calculado (tipo_documento + num_documento)
      },
      {
        accessorKey: "primer_apellido", // Campo real de BD para sorting
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("pacientes.columns.name")} />
        ),
        cell: ({ row }) => (
          <button
            type="button"
            className="font-medium text-primary hover:underline cursor-pointer text-left"
            onClick={() => handleVerHistorial(row.original)}
            title={t("busquedaAvanzada.verHistorial", "Ver historial completo")}
          >
            {row.original.nombre_completo}
          </button>
        ),
      },
      {
        accessorKey: "edad_texto",
        header: t("pacientes.columns.age"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.edad_texto}</span>
        ),
        enableSorting: false, // Campo calculado
      },
      {
        accessorKey: "sexo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("pacientes.columns.gender")} />
        ),
        cell: ({ row }) => (
          <Badge variant="outline">
            {row.original.sexo === "M" ? t("pacientes.gender.male") : t("pacientes.gender.female")}
          </Badge>
        ),
      },
      {
        accessorKey: "celular",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("pacientes.columns.cellphone")} />
        ),
        cell: ({ row }) => <span className="text-sm">{row.original.celular || "-"}</span>,
      },
      {
        accessorKey: "is_active",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("pacientes.columns.status")} />
        ),
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? "default" : "secondary"}>
            {row.original.is_active ? t("common.active") : t("common.inactive")}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const paciente = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(paciente)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleViewConsentimientos(paciente)}>
                  <FileSignature className="mr-2 h-4 w-4" />
                  {t("pacientes.viewConsents", "Consentimientos")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleViewPatologias(paciente)}>
                  <Activity className="mr-2 h-4 w-4" />
                  {t("pacientes.viewPatologias", "Patologías")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleViewArchivos(paciente)}>
                  <Paperclip className="mr-2 h-4 w-4" />
                  {t("pacientes.viewArchivos", "Archivos Adjuntos")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleVerHistorial(paciente)}>
                  <History className="mr-2 h-4 w-4" />
                  {t("busquedaAvanzada.verHistorial", "Ver historial completo")}
                </DropdownMenuItem>
                {paciente.is_active && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleNuevaOrden(paciente)}>
                      <ClipboardList className="mr-2 h-4 w-4" />
                      {t("pacientes.newOrder", "Nueva Orden")}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(paciente)}>
                  {paciente.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!paciente.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(paciente)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [
      t,
      isTablet,
      handleEdit,
      handleViewConsentimientos,
      handleViewPatologias,
      handleViewArchivos,
      handleVerHistorial,
      handleNuevaOrden,
      handleToggleStatus,
      handleDelete,
    ]
  );

  // Server-side data table hook
  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getPacientes,
    queryKey: "pacientes",
    storageKey: "pacientes_page_size",
    defaultPageSize: 20,
    filters: [{ key: "is_active", defaultValue: "all", urlParam: "status" }],
  });

  // Column labels for view options
  const columnLabels = {
    primer_apellido: t("pacientes.columns.name"),
    edad_texto: t("pacientes.columns.age"),
    sexo: t("pacientes.columns.gender"),
    celular: t("pacientes.columns.cellphone"),
    is_active: t("pacientes.columns.status"),
  };

  // Mobile card render function
  const renderMobileCard = useCallback(
    (paciente: PacienteListItem) => (
      <div className="flex items-start gap-3">
        <PatientAvatar
          src={paciente.foto_documento_url}
          name={paciente.nombre_completo}
          sexo={paciente.sexo}
          size="md"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              className="font-medium truncate text-primary hover:underline cursor-pointer text-left"
              onClick={() => handleVerHistorial(paciente)}
              title={t("busquedaAvanzada.verHistorial", "Ver historial completo")}
            >
              {paciente.nombre_completo}
            </button>
            <Badge variant={paciente.is_active ? "default" : "secondary"} className="shrink-0">
              {paciente.is_active ? t("common.active") : t("common.inactive")}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground font-mono">{paciente.identificacion}</p>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            <span>{paciente.edad_texto}</span>
            <span>•</span>
            <span>
              {paciente.sexo === "M" ? t("pacientes.gender.male") : t("pacientes.gender.female")}
            </span>
          </div>
          {paciente.celular && (
            <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
              <Phone className="h-3 w-3" />
              <span>{paciente.celular}</span>
            </div>
          )}
          <div className="flex gap-1 mt-3">
            <Button variant="outline" size="sm" onClick={() => handleEdit(paciente)}>
              <Pencil className="h-3 w-3 mr-1" />
              {t("common.edit")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleViewConsentimientos(paciente)}>
                  <FileSignature className="mr-2 h-4 w-4" />
                  {t("pacientes.viewConsents", "Consentimientos")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleViewPatologias(paciente)}>
                  <Activity className="mr-2 h-4 w-4" />
                  {t("pacientes.viewPatologias", "Patologías")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleViewArchivos(paciente)}>
                  <Paperclip className="mr-2 h-4 w-4" />
                  {t("pacientes.viewArchivos", "Archivos Adjuntos")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleVerHistorial(paciente)}>
                  <History className="mr-2 h-4 w-4" />
                  {t("busquedaAvanzada.verHistorial", "Ver historial completo")}
                </DropdownMenuItem>
                {paciente.is_active && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleNuevaOrden(paciente)}>
                      <ClipboardList className="mr-2 h-4 w-4" />
                      {t("pacientes.newOrder", "Nueva Orden")}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(paciente)}>
                  {paciente.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!paciente.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(paciente)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    ),
    [
      t,
      handleEdit,
      handleViewConsentimientos,
      handleViewPatologias,
      handleViewArchivos,
      handleVerHistorial,
      handleNuevaOrden,
      handleToggleStatus,
      handleDelete,
    ]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t("pacientes.title")}</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("pacientes.description")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button size={isMobile ? "sm" : "default"} onClick={handleCreate}>
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("pacientes.newPatient")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("pacientes.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex gap-2">
              <Select
                value={filters.is_active}
                onValueChange={(value) => setFilter("is_active", value)}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder={t("pacientes.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pacientes.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("common.active")}</SelectItem>
                  <SelectItem value="false">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="secondary" size="icon" onClick={clearFilters} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>
              {t("pacientes.showingResults", { count: totalCount })}
            </CardDescription>
            {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("pacientes.noPacientes")}
            hideOnTablet={["celular", "edad_texto", "sexo"]}
            renderMobileCard={renderMobileCard}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <PacienteFormDialog
        open={isFormOpen && isFormReady}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) {
            setSelectedPacienteId(null);
          }
        }}
        paciente={selectedPaciente || null}
        onSuccess={handleFormSuccess}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("pacientes.confirmActivate")
            : confirmAction === "delete"
              ? t("pacientes.confirmDelete")
              : t("pacientes.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("pacientes.confirmActivateDesc", {
                name: selectedPacienteForAction?.nombre_completo,
              })
            : confirmAction === "delete"
              ? t("pacientes.confirmDeleteDesc", {
                  name: selectedPacienteForAction?.nombre_completo,
                })
              : t("pacientes.confirmDeactivateDesc", {
                  name: selectedPacienteForAction?.nombre_completo,
                })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />

      <ConfirmDialog
        open={isCreateOrdenPromptOpen}
        onOpenChange={(open) => {
          setIsCreateOrdenPromptOpen(open);
          if (!open) setNewlyCreatedPaciente(null);
        }}
        title={t("pacientes.createOrderPrompt", "¿Crear orden para este paciente?")}
        description={t(
          "pacientes.createOrderPromptDesc",
          "El paciente {{name}} fue creado exitosamente. ¿Desea crear una orden de exámenes?",
          { name: newlyCreatedPaciente?.nombre_completo }
        )}
        confirmText={t("pacientes.yesCreateOrder", "Sí, crear orden")}
        cancelText={t("common.no", "No")}
        onConfirm={() => {
          if (newlyCreatedPaciente) {
            navigate("/ordenes", {
              state: {
                pacienteId: newlyCreatedPaciente.id,
                pacienteNombre: newlyCreatedPaciente.nombre_completo,
                pacienteDesc: newlyCreatedPaciente.identificacion
                  ? [
                      newlyCreatedPaciente.identificacion,
                      newlyCreatedPaciente.edad_texto,
                      newlyCreatedPaciente.celular ? `Tel: ${newlyCreatedPaciente.celular}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : "",
                pacienteEdadYears: newlyCreatedPaciente.edad_years,
              },
            });
          }
          setIsCreateOrdenPromptOpen(false);
          setNewlyCreatedPaciente(null);
        }}
      />

      {/* Consentimientos Sheet */}
      {selectedPacienteForAction && (
        <Sheet open={isConsentimientosOpen} onOpenChange={setIsConsentimientosOpen}>
          <SheetContent className="sm:max-w-[600px] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{t("consentimientos.title", "Consentimientos Informados")}</SheetTitle>
              <SheetDescription>{selectedPacienteForAction.nombre_completo}</SheetDescription>
            </SheetHeader>
            <div className="mt-6">
              <ConsentimientosList
                pacienteId={selectedPacienteForAction.id}
                pacienteNombre={selectedPacienteForAction.nombre_completo}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Patologías Sheet */}
      {selectedPacienteForAction && (
        <Sheet open={isPatologiasOpen} onOpenChange={setIsPatologiasOpen}>
          <SheetContent className="sm:max-w-[600px] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{t("patologias.title", "Patologías / Historial Clínico")}</SheetTitle>
              <SheetDescription>{selectedPacienteForAction.nombre_completo}</SheetDescription>
            </SheetHeader>
            <div className="mt-6">
              <PatologiasList
                pacienteId={selectedPacienteForAction.id}
                pacienteNombre={selectedPacienteForAction.nombre_completo}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Archivos Adjuntos Sheet */}
      {selectedPacienteForAction && (
        <Sheet open={isArchivosOpen} onOpenChange={setIsArchivosOpen}>
          <SheetContent className="sm:max-w-[700px] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{t("archivosAdjuntos.title", "Archivos Adjuntos")}</SheetTitle>
              <SheetDescription>{selectedPacienteForAction.nombre_completo}</SheetDescription>
            </SheetHeader>
            <div className="mt-6">
              <ArchivosAdjuntosList
                pacienteId={selectedPacienteForAction.id}
                pacienteNombre={selectedPacienteForAction.nombre_completo}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Historial completo - Búsqueda Avanzada Sheet */}
      <BusquedaAvanzadaSheet
        open={historialOpen}
        onOpenChange={setHistorialOpen}
        initialParams={
          historialPaciente ? { identidad: historialPaciente.num_documento } : undefined
        }
        autoSearch={true}
      />
    </div>
  );
}
